import type { Rol } from "@copi/shared";
import { Navigate, useLocation } from "react-router-dom";

import { paths } from "@/app/paths";
import { useAuth } from "@/features/auth/auth.context";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Si se omite, basta con estar autenticado. */
  roles?: readonly Rol[];
  /**
   * Capacidades del docente requeridas (ADR 054). Si se da, el usuario debe
   * tener al menos una. Se combina en AND con `roles` cuando ambos están
   * presentes; en la práctica cada ruta usa uno u otro.
   */
  capacidades?: ReadonlyArray<"asesor" | "jurado">;
}

/**
 * Envuelve rutas que requieren autenticación. Si `roles` está presente,
 * además verifica que el usuario tenga uno de esos roles.
 *
 * Comportamiento:
 *  - Cargando → splash mínimo.
 *  - No autenticado → redirige a /login conservando la ruta original.
 *  - Rol incorrecto → redirige a "/" (la raíz despacha al home del rol). Antes
 *    mostraba una pantalla "Acceso denegado" sin salida: tras loguear con un
 *    rol distinto al de la ruta previa (p. ej. asesor → decanato), el usuario
 *    quedaba atrapado en un 403 muerto. Redirigir al home del rol lo resuelve.
 */
export function ProtectedRoute({ children, roles, capacidades }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background"
        role="status"
        aria-live="polite"
      >
        <span className="text-sm text-muted-foreground">Cargando sesión…</span>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const rolOk = !roles || roles.includes(user.rol);
  const capacidadOk =
    !capacidades ||
    capacidades.some((c) => (c === "asesor" ? user.puedeAsesorar : user.puedeSerJurado));
  if (!rolOk || !capacidadOk) {
    // El rol/capacidad no puede ver esta ruta → al dashboard (`/inicio`), que
    // despacha por rol. Evita la pantalla 403 muerta tras cambiar de usuario/rol.
    // (La raíz `/` es la landing pública desde ADR 051, vamos directo al dashboard.)
    return <Navigate to={paths.dashboard()} replace />;
  }

  return <>{children}</>;
}
